import * as React from "react";
import { cn } from "../../lib/utils";

type RadioGroupContextValue = {
  name: string;
  value?: string;
  onValueChange?: (value: string) => void;
};

const RadioGroupContext = React.createContext<RadioGroupContextValue | null>(null);

interface RadioGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string;
  onValueChange?: (value: string) => void;
  name?: string;
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ className, value, onValueChange, name, children, ...props }, ref) => {
    const generatedName = React.useId();
    return (
      <RadioGroupContext.Provider value={{ name: name ?? generatedName, value, onValueChange }}>
        <div ref={ref} className={cn("grid gap-2", className)} {...props}>
          {children}
        </div>
      </RadioGroupContext.Provider>
    );
  },
);
RadioGroup.displayName = "RadioGroup";

interface RadioGroupItemProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "onChange"> {
  value: string;
}

const RadioGroupItem = React.forwardRef<HTMLInputElement, RadioGroupItemProps>(({ className, value, id, disabled, ...props }, ref) => {
  const ctx = React.useContext(RadioGroupContext);
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const checked = ctx?.value === value;

  return (
    <input
      ref={ref}
      id={inputId}
      type="radio"
      name={ctx?.name}
      value={value}
      checked={checked}
      disabled={disabled}
      onChange={() => ctx?.onValueChange?.(value)}
      className={cn("h-4 w-4 accent-current focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50", className)}
      {...props}
    />
  );
});
RadioGroupItem.displayName = "RadioGroupItem";

export { RadioGroup, RadioGroupItem };
